import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { TILE_SIZE, mapViewport } from '../../utils/mercator';
import './LocationsMap.css';

/** Потолок пропорции кадра на узком экране: шире — и он вырождается в полосу. */
const NARROW_RATIO_CAP = 1.2;

/** Поле вокруг точек на широком кадре — в его собственных координатах. */
const WIDE_PADDING = 72;

/** То же поле на узком — долей ширины кадра, см. комментарий у `padding`. */
const NARROW_PADDING_RATIO = 0.18;

/**
 * Карта салонов: один кадр, метка на каждый адрес, выбор связан с карточками.
 *
 * ## Почему карта сложена из тайлов, а не вставлена фреймом
 *
 * Встраиваемый OSM (`export/embed.html`) принимает bbox, но показывает не
 * его: Leaflet внутри подбирает ближайший ЦЕЛЫЙ зум и центрирует вид. Какой
 * зум он выбрал, снаружи не узнать — фрейм с чужого домена. Пока точка одна и
 * bbox симметричен вокруг неё, метку можно положить в центр кадра и не
 * ошибиться (так работал прежний `MapEmbed`, снят в #1335). Для двух точек
 * это разваливается: метка, положенная приблизительно, на карте Европы
 * промахивается на страну.
 *
 * Плюс во фрейм нельзя достучаться: клик по чужой метке нам не виден, а
 * связка «выбрал метку — подсветилась карточка» и есть смысл этого блока.
 *
 * Поэтому тайлы кладём сами (`utils/mercator`), а метки — обычные кнопки
 * поверх. Зум и начало координат известны, метки встают точно, клики наши.
 *
 * ## Размер кадра
 *
 * Он нужен ДО отрисовки, чтобы посчитать зум, поэтому задаётся пропсами, а
 * не измеряется. Кадр масштабируется в CSS (`width: 100%`), картинка тайлов
 * при этом растягивается пропорционально вместе с метками — они позиционируются
 * в процентах, а не в пикселях.
 *
 * ## На телефоне кадр становится ближе к квадрату
 *
 * Пропорция приходит из раскладки страницы и она широкая: 1440×560 на
 * «Шоурумах» это 2.6:1. На 320px такая полоса даёт кадр высотой 124px —
 * замер, — а проекция при этом вписывает обе точки в почти плоскую щель и
 * отъезжает до карты Европы с Африкой. Ниже 640px высота считается из ширины
 * по потолку 1.2:1: на 320px это 267px, метки остаются в кадре, зум
 * человеческий.
 *
 * Медиазапрос ЗДЕСЬ, в JS, а не в CSS-файле: от ширины зависит не стиль, а
 * содержимое — число, из которого считается зум и сетка тайлов. CSS-правило
 * растянуло бы готовый кадр по вертикали, то есть исказило бы проекцию.
 *
 * @param {object} props
 * @param {Array<{name:string, coords:{lat:number, lon:number}}>} props.locations
 * @param {string} [props.selected] — имя выбранного салона.
 * @param {(name: string) => void} [props.onSelect]
 * @param {number} [props.width] — расчётная ширина кадра.
 * @param {number} [props.height] — расчётная высота кадра.
 */
export const LocationsMap = ({
  locations,
  selected,
  onSelect,
  width = 640,
  height = 420,
}) => {
  const points = locations.filter((l) => l.coords);

  const narrow = useMediaQuery('(max-width: 639px)');
  const frameHeight = narrow ? Math.round(width / NARROW_RATIO_CAP) : height;

  /**
   * Поле вокруг точек — долей кадра, а не фиксированными пикселями.
   *
   * 72px считаются в координатах кадра (1440 широкий), а показывается он на
   * 320 — то есть на экране от них остаётся 16px, вдвое меньше самой метки. На
   * квадратном кадре ограничение переезжает на горизонталь, и обе метки
   * прижимались к кромкам: замер на 320 дал 6.9% и 93.1% по ширине, подпись
   * левой уходила за кадр на 18px. 18% ширины дают на экране те же ~58px,
   * что и 72 на десктопе.
   */
  const padding = narrow
    ? Math.round(width * NARROW_PADDING_RATIO)
    : WIDE_PADDING;

  const view = useMemo(
    () =>
      points.length
        ? mapViewport(
            points.map((l) => l.coords),
            width,
            frameHeight,
            { padding, maxZoom: 11 }
          )
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(points.map((l) => l.coords)), width, frameHeight, padding]
  );

  // Салон без координат на карте не покажешь — блок целиком не рендерится,
  // а адреса рядом остаются: карта здесь уточняет, а не заменяет их.
  if (!view) return null;

  const { zoom, originX, originY, pins } = view;

  // Сетка тайлов, покрывающая кадр. Считаем от левого верхнего угла: первый
  // тайл почти всегда обрезан, поэтому его смещение отрицательное.
  const firstCol = Math.floor(originX / TILE_SIZE);
  const firstRow = Math.floor(originY / TILE_SIZE);
  const lastCol = Math.floor((originX + width) / TILE_SIZE);
  const lastRow = Math.floor((originY + frameHeight) / TILE_SIZE);
  const worldTiles = 2 ** zoom;

  const tiles = [];
  for (let row = firstRow; row <= lastRow; row += 1) {
    for (let col = firstCol; col <= lastCol; col += 1) {
      // По долготе мир замкнут — колонки заворачиваются. По широте нет:
      // строки за краем мира не существуют, и запрос вернул бы 404.
      if (row < 0 || row >= worldTiles) continue;
      const wrapped = ((col % worldTiles) + worldTiles) % worldTiles;
      tiles.push({
        key: `${row}-${col}`,
        src: `https://tile.openstreetmap.org/${zoom}/${wrapped}/${row}.png`,
        left: col * TILE_SIZE - originX,
        top: row * TILE_SIZE - originY,
      });
    }
  }

  return (
    <div
      className="locations-map"
      style={{ aspectRatio: `${width} / ${frameHeight}` }}
    >
      <div className="locations-map__tiles" aria-hidden="true">
        {tiles.map((tile) => (
          <img
            key={tile.key}
            className="locations-map__tile"
            src={tile.src}
            alt=""
            loading="lazy"
            style={{
              left: `${(tile.left / width) * 100}%`,
              top: `${(tile.top / frameHeight) * 100}%`,
              width: `${(TILE_SIZE / width) * 100}%`,
              height: `${(TILE_SIZE / frameHeight) * 100}%`,
            }}
          />
        ))}
      </div>

      {points.map((location, index) => {
        const isSelected = selected === location.name;
        return (
          <button
            key={location.name}
            type="button"
            className={`locations-map__pin${
              isSelected ? ' locations-map__pin--selected' : ''
            }`}
            style={{
              left: `${(pins[index].x / width) * 100}%`,
              top: `${(pins[index].y / frameHeight) * 100}%`,
            }}
            aria-pressed={isSelected}
            onClick={() => onSelect?.(location.name)}
          >
            <span className="locations-map__pin-dot" aria-hidden="true" />
            <span className="locations-map__pin-label">{location.name}</span>
          </button>
        );
      })}

      {/* Атрибуция обязательна по условиям OSM и не прячется: тайлы чужие. */}
      <a
        className="locations-map__credit"
        href="https://www.openstreetmap.org/copyright"
        target="_blank"
        rel="noopener noreferrer"
      >
        © OpenStreetMap
      </a>
    </div>
  );
};

LocationsMap.propTypes = {
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      coords: PropTypes.shape({ lat: PropTypes.number, lon: PropTypes.number }),
    })
  ).isRequired,
  selected: PropTypes.string,
  onSelect: PropTypes.func,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default LocationsMap;
